// The app shell (docs/ARCHITECTURE.md §2): the map, the side panel (search, tabs, filters, badge, the list; at phone
// width the controls float on top and the list is a bar at the bottom) and the map's buttons, each in its own error
// boundary so one failing part can't blank the rest. Starts the clock, the fetching and the profile sync.
import { useEffect } from "react";
import { FALLBACK_CENTER, START_ZOOM } from "../data/config";
import { loadEvents } from "../data/load";
import { SHARED } from "../shared/config";
import { FilterBar } from "../features/filters/FilterBar";
import { TimeTabs } from "../features/filters/TimeTabs";
import { NearbyList } from "../features/nearby/NearbyList";
import { SearchBox } from "../features/search/SearchBox";
import { EventMap } from "../map/EventMap";
import { Sheet } from "../features/card/Sheet";
import { closeLevelMenu, HideToast } from "../features/profile/slots";
import { startClock, tick } from "../state/clock";
import { getProfile } from "../state/profile";
import { accountOpen } from "../profile/account";
import { Note } from "../ui/Note";
import { idInPath, startUrlSync } from "../state/url";
import { closeSheet, FIRST_TAB, moveMap, setTab, useView, viewStore } from "../state/view";
import { Badge } from "./Badge";
import { ErrorBoundary, Region } from "./ErrorBoundary";
import { locate } from "./location";
import { MapButtons } from "./MapButtons";
import styles from "./App.module.css";

/** Nothing has moved the map yet: no card open, no event in the address, the camera where it started. */
function stillAtStart() {
  const v = viewStore.getState(), c = v.camera?.center;
  const atStart = !c || (Math.abs(c.lat - FALLBACK_CENTER.lat) < 1e-6 && Math.abs(c.lng - FALLBACK_CENTER.lng) < 1e-6);
  return !v.sheet && !idInPath() && atStart && !v.move;
}

function useStartup() {
  useEffect(() => {
    startClock();
    void loadEvents("full");
    // Centre on the browser's location if allowed (once), otherwise stay on St. Pete; but not once something else has
    // placed the map (a shared event's link, a card opened, the map moved by hand).
    locate((l) => { if (stillAtStart()) moveMap(l, 13); });
    const refresh = setInterval(() => { if (document.visibilityState === "visible") void loadEvents("refresh"); }, SHARED.fetch.refreshMinutes * 60e3);
    // Coming back to the tab: opens on Today every time, with fresh events.
    const visible = () => {
      if (document.visibilityState !== "visible") return;
      tick();
      setTab(FIRST_TAB);
      void loadEvents("refresh");
    };
    document.addEventListener("visibilitychange", visible);
    // The profile syncs only where personal features work (personal mode); the public site never asks for it.
    const stopProfile = accountOpen() ? getProfile().start() : () => {};
    const stopUrl = startUrlSync();
    // Escape closes the innermost thing: a type's level menu, then a dropdown (FilterBar), then the sheet.
    const escape = (ev: KeyboardEvent) => {
      if (ev.key !== "Escape" || ev.defaultPrevented || viewStore.getState().popover) return;
      if (closeLevelMenu()) return;
      closeSheet();
    };
    document.addEventListener("keydown", escape);
    return () => {
      clearInterval(refresh); document.removeEventListener("visibilitychange", visible); document.removeEventListener("keydown", escape); stopProfile(); stopUrl();
    };
  }, []);
}

export function App() {
  useStartup();
  const sheetOpen = useView((s) => !!s.sheet);
  useEffect(() => { document.body.classList.toggle("sheet-open", sheetOpen); }, [sheetOpen]);
  return (
    <>
      <ErrorBoundary region="map">
        <EventMap center={FALLBACK_CENTER} zoom={START_ZOOM} />
      </ErrorBoundary>
      <aside id="panel" className={styles.panel}>
        <div id="controls" className={styles.controls}>
          <Region name="search"><SearchBox /></Region>
          <Region name="time tabs"><TimeTabs /></Region>
          <Region name="filters"><FilterBar /></Region>
          <Region name="badge"><Badge /></Region>
        </div>
        <Region name="list"><NearbyList /></Region>
        <Region name="sheet"><Sheet /></Region>
      </aside>
      <Region name="map buttons"><MapButtons /></Region>
      <Region name="toast"><HideToast /></Region>
      <Region name="note"><Note /></Region>
    </>
  );
}
